import { useState, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { getDemoServicesForBusiness } from '../utils/demoData';
import toast from 'react-hot-toast';

export const useServices = () => {
  const { masterDb, activeBusinessId, setMasterDb } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isDemo = ['9001', '9002', '9003'].includes(String(activeBusinessId));
  const isReadOnlyDemo = isDemo && userRole !== 'master';

  const activeBusiness = Array.isArray(masterDb) ? masterDb.find(b => String(b.id) === String(activeBusinessId)) : null;
  
  const services = useMemo(() => {
    const rawList = Array.isArray(activeBusiness?.services) ? activeBusiness.services : [];
    if (rawList.length > 0) return rawList;
    return isDemo ? getDemoServicesForBusiness(activeBusinessId) : [];
  }, [activeBusiness, isDemo, activeBusinessId]);
  
  const [isProcessing, setIsProcessing] = useState(false);

  const addService = async (newService, onSuccess) => {
    if (isReadOnlyDemo) {
      toast.error("Modo solo lectura: Las cuentas demo no se pueden modificar.");
      return;
    }

    setIsProcessing(true);
    if (!newService.nombre?.trim()) {
      toast.error("Por favor ingresa el nombre del servicio");
      setIsProcessing(false);
      return;
    }
    
    const isCotizar = Boolean(newService.precio_cotizar || newService.precio === null || newService.precio === undefined || newService.precio === "");
    const finalPrice = isCotizar ? 0 : (newService.precio !== undefined && newService.precio !== "" ? Number(newService.precio) : 0);

    const service = { 
      id: newService.id || Date.now().toString(), 
      business_id: String(activeBusinessId),
      nombre: newService.nombre.trim(),
      precio: finalPrice,
      precio_cotizar: isCotizar,
      duracion_minutos: newService.duracion_minutos || "30",
      activo: newService.activo !== undefined ? newService.activo : true,
      descripcion: newService.descripcion || null,
      is_popular: Boolean(newService.is_popular)
    };
    
    // Optimistic Update Local (conserva precio_cotizar para React UI)
    setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return { ...b, services: [...(Array.isArray(b.services) ? b.services : []), service] };
      }
      return b;
    }));

    try {
      // Filtrar estrictamente sólo las columnas reales de la tabla 'services' en Supabase Postgres
      const dbPayload = {
        id: service.id,
        business_id: service.business_id,
        nombre: service.nombre,
        precio: service.precio,
        duracion_minutos: service.duracion_minutos,
        activo: service.activo,
        descripcion: service.descripcion,
        is_popular: service.is_popular
      };

      const { error } = await supabase.from('services').insert([dbPayload]);
      if (error) {
        console.warn("[Supabase Warning]", error.message);
        toast.error("Error guardando en la nube: " + error.message);
      } else {
        toast.success("¡Servicio agregado exitosamente!");
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error(err);
      toast("Agregado localmente (Sin conexión).", { icon: '⚠️' });
    } finally {
      setIsProcessing(false);
    }
  };

  const updateService = async (serviceId, updates, onSuccess) => {
    if (isReadOnlyDemo) {
      toast.error("Modo solo lectura: Las cuentas demo no se pueden modificar.");
      return;
    }

    setIsProcessing(true);
    if (updates.nombre !== undefined && !updates.nombre.trim()) {
      toast.error("El nombre no puede estar vacío.");
      setIsProcessing(false);
      return;
    }
    
    // Optimistic Update Local
    setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          services: (Array.isArray(b.services) ? b.services : []).map(s => String(s.id) === String(serviceId) ? { ...s, ...updates } : s)
        };
      }
      return b;
    }));

    try {
      // Filtrar columnas inexistentes en Postgres antes de enviar update
      const { precio_cotizar, ...dbUpdates } = updates;
      if (precio_cotizar !== undefined && dbUpdates.precio === undefined) {
        if (precio_cotizar) dbUpdates.precio = 0;
      }
      if (dbUpdates.precio === null) dbUpdates.precio = 0;

      const { error } = await supabase.from('services').update(dbUpdates).eq('id', serviceId);
      if (error) {
        toast.error("Error al actualizar: " + error.message);
      } else {
        toast.success("¡Actualizado exitosamente!");
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      console.error("Error al actualizar servicio:", err);
      toast("Actualizado localmente.", { icon: '⚠️' });
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteService = async (serviceId) => {
    if (isReadOnlyDemo) {
      toast.error('Acción denegada: No se pueden eliminar servicios en cuentas de demostración para visitantes.');
      return;
    }
    
    setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return { ...b, services: (Array.isArray(b.services) ? b.services : []).filter(s => String(s.id) !== String(serviceId)) };
      }
      return b;
    }));

    try {
      const { error } = await supabase.from('services').delete().eq('id', serviceId);
      if (error) {
        console.error("Error al eliminar servicio en Supabase:", error);
        toast.error("Error al eliminar de la nube");
      } else {
        toast("Eliminado", { icon: '🗑️' });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const toggleServiceStatus = async (serviceId) => {
    if (isReadOnlyDemo) {
      toast.error('Modo solo lectura: Las cuentas demo no se pueden modificar.');
      return;
    }

    const srv = services.find(s => String(s.id) === String(serviceId));
    if (!srv) return;
    const newActive = !srv.activo;

    setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return {
          ...b,
          services: (Array.isArray(b.services) ? b.services : []).map(s => String(s.id) === String(serviceId) ? { ...s, activo: newActive } : s)
        };
      }
      return b;
    }));

    try {
      await supabase.from('services').update({ activo: newActive }).eq('id', serviceId);
      toast.success("Estado del servicio modificado");
    } catch (err) {
      console.error(err);
    }
  };

  return {
    services,
    activeBusiness,
    isProcessing,
    addService,
    updateService,
    deleteService,
    toggleServiceStatus
  };
};
